// frontend/src/components/referee/RefereeManager.jsx — Kampfrichtereinteilung
//
// NEU GEBAUT (25.09.2026)
//
// Die alte Ansicht war nicht benutzbar:
// - Sie bot Personen aus `users` mit der Rolle 'kampfrichter' an. Solche
//   Benutzer lassen sich nirgends anlegen, es gab nur sechs Demo-Datensätze.
//   Die echten Kampfrichter stehen in `kampfrichter` (Reiter "Kampfrichter").
// - `loadAssignments` legte die ganze Antwort { success, data } in den
//   Zustand, wo eine Liste erwartet wird → `.filter` ist keine Funktion.
// - "Zuteilen" war ohne `scheduleId` gesperrt, und kein Aufrufer gab eine mit.
// - Start und Ende waren Pflicht, der Ring eine freie Zahl von 1 bis 10.
//
// Jetzt: Kampfrichter-Konto + Kampffläche des Turniers + Rolle, Zeit
// freiwillig. Dieselbe Antwort liest der Informationsbildschirm
// (/kampfrichter-display/:turnierId), der hier geöffnet wird.
//
// Die Verfügbarkeiten und die Auto-Zuteilung der alten Ansicht hängen weiter
// an `users` und sind deshalb hier nicht mehr angeboten.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../dashboard/RegStaffManager.css';
import './RefereeManager.css';

/** fetch mit Anmeldung — eine Stelle, an der der Token gesetzt wird. */
async function hole(pfad, optionen = {}) {
  const token = localStorage.getItem('token');
  return fetch(pfad, {
    ...optionen,
    headers: {
      ...(optionen.body ? { 'Content-Type': 'application/json' } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(optionen.headers || {}),
    },
  });
}

export const ROLLEN = [
  { value: 'hauptkampfrichter',  label: 'Hauptkampfrichter' },
  { value: 'seitenkampfrichter', label: 'Seitenkampfrichter' },
  { value: 'punktrichter',       label: 'Punktrichter' },
  { value: 'zeitnehmer',         label: 'Zeitnehmer' },
];
const rollenName = (r) => ROLLEN.find(x => x.value === r)?.label || r;

/** "2026-09-25T09:00" → "09:00" (Text, keine Zeitzonenrechnung) */
const uhrzeit = (s) => (s ? s.slice(11, 16) : '');

const LEER = { kampfrichter_id: '', kampfflaeche_id: '', role: 'hauptkampfrichter',
               assignment_start: '', assignment_end: '', notes: '' };

const RefereeManager = ({ turnierId }) => {
  const [konten, setKonten] = useState([]);
  const [einteilung, setEinteilung] = useState({ flaechen: [], zuteilungen: [] });
  const [form, setForm] = useState(LEER);
  const [mitZeit, setMitZeit] = useState(false);
  const [laedt, setLaedt] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const [fehler, setFehler] = useState('');
  const [kopiert, setKopiert] = useState(false);

  const laden = useCallback(async () => {
    if (!turnierId) return;
    setLaedt(true);
    try {
      const [rk, re] = await Promise.all([
        hole(`/api/kampfrichter/list?turnier_id=${turnierId}`),
        hole(`/api/schedule/tournament/${turnierId}/assignments`),
      ]);
      if (rk.status === 401 || re.status === 401) { setFehler('Nicht angemeldet.'); return; }
      if (rk.status === 403 || re.status === 403) { setFehler('Nur für Administratoren.'); return; }
      const dk = await rk.json();
      const de = await re.json();
      setKonten(Array.isArray(dk?.data) ? dk.data : []);
      setEinteilung({
        flaechen: Array.isArray(de?.data?.flaechen) ? de.data.flaechen : [],
        zuteilungen: Array.isArray(de?.data?.zuteilungen) ? de.data.zuteilungen : [],
      });
    } catch (e) {
      console.error('Einteilung laden:', e);
      setFehler('Einteilung konnte nicht geladen werden.');
    } finally {
      setLaedt(false);
    }
  }, [turnierId]);

  useEffect(() => { laden(); }, [laden]);

  const zuteilen = async (e) => {
    e.preventDefault();
    setFehler('');
    setSpeichert(true);
    try {
      const r = await hole('/api/schedule/assignments', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          turnier_id: turnierId,
          assignment_start: mitZeit ? form.assignment_start : '',
          assignment_end: mitZeit ? form.assignment_end : '',
        }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) { setFehler(d.message || 'Zuteilung konnte nicht gespeichert werden.'); return; }
      // Fläche, Rolle und Zeit stehen lassen — meist teilt man mehrere
      // Kampfrichter nacheinander derselben Fläche zu.
      setForm(f => ({ ...f, kampfrichter_id: '', notes: '' }));
      laden();
    } catch (e2) {
      setFehler('Zuteilung konnte nicht gespeichert werden.');
    } finally {
      setSpeichert(false);
    }
  };

  const entfernen = async (z) => {
    if (!window.confirm(`${z.referee_name} von ${z.flaeche_name || `Ring ${z.ring_number}`} nehmen?`)) return;
    const r = await hole(`/api/schedule/assignments/${z.assignment_id}`, { method: 'DELETE' });
    if (!r.ok) { setFehler('Zuteilung konnte nicht entfernt werden.'); return; }
    laden();
  };

  const displayAdresse = `${window.location.origin}/kampfrichter-display/${turnierId}`;
  const bildschirmOeffnen = () => window.open(displayAdresse, 'tda-kampfrichter-display');
  const adresseKopieren = () => {
    navigator.clipboard?.writeText(displayAdresse).then(() => {
      setKopiert(true);
      setTimeout(() => setKopiert(false), 2000);
    });
  };

  const aktiveKonten = konten.filter(k => k.aktiv);
  const eingeteilt = useMemo(
    () => new Set(einteilung.zuteilungen.map(z => z.kampfrichter_id).filter(Boolean)),
    [einteilung.zuteilungen]
  );
  const ohneFlaeche = aktiveKonten.filter(k => !eingeteilt.has(k.id));

  // Zuteilungen je Fläche; alte Zeilen ohne Flächenverweis nach Ringnummer.
  const jeFlaeche = useMemo(() => {
    const gruppen = einteilung.flaechen.map(f => ({
      schluessel: `f${f.kampfflaeche_id}`, titel: f.name, farbe: f.farbe,
      liste: einteilung.zuteilungen.filter(z => z.kampfflaeche_id === f.kampfflaeche_id),
    }));
    const ohne = einteilung.zuteilungen.filter(z => !z.kampfflaeche_id);
    if (ohne.length) gruppen.push({ schluessel: 'ohne', titel: 'Ohne Kampffläche', farbe: '#888', liste: ohne });
    return gruppen;
  }, [einteilung]);

  if (!turnierId) {
    return <div className="rsm-wrap"><div className="rsm-empty">Bitte zuerst ein Turnier wählen.</div></div>;
  }

  return (
    <div className="rsm-wrap">
      <div className="rsm-header">
        <div className="rsm-header-left">
          <span className="rsm-header-icon">🗂</span>
          <div>
            <div className="rsm-title">Kampfrichtereinteilung</div>
            <div className="rsm-subtitle">Wer an welcher Kampffläche steht</div>
          </div>
        </div>
        <div className="rsm-header-right">
          <button className="rsm-btn rsm-btn-primary" onClick={bildschirmOeffnen}>
            Informationsbildschirm öffnen
          </button>
          <button className="rsm-btn rsm-btn-ghost" onClick={adresseKopieren} title={displayAdresse}>
            {kopiert ? '✓ Kopiert' : 'Adresse kopieren'}
          </button>
          <button className="rsm-btn rsm-btn-ghost" onClick={laden} disabled={laedt}>
            {laedt ? '…' : '↻'}
          </button>
        </div>
      </div>

      {fehler && <div className="rsm-error">{fehler}</div>}

      {einteilung.flaechen.length === 0 && !laedt ? (
        <div className="rsm-empty">
          Dieses Turnier hat noch keine Kampfflächen. Lege sie zuerst an, dann kannst du einteilen.
        </div>
      ) : aktiveKonten.length === 0 && !laedt ? (
        <div className="rsm-empty">
          Noch keine aktiven Kampfrichter. Lege sie im Reiter „Kampfrichter“ an.
        </div>
      ) : (
        <div className="rsm-form-box">
          <div className="rsm-form-box-title">Kampfrichter zuteilen</div>
          <form className="rsm-new-form" onSubmit={zuteilen}>
            <div className="rsm-form-row">
              <div className="rsm-field">
                <label className="rsm-label">Kampfrichter *</label>
                <select className="rsm-input" required value={form.kampfrichter_id}
                        onChange={e => setForm({ ...form, kampfrichter_id: e.target.value })}>
                  <option value="">auswählen …</option>
                  {aktiveKonten.map(k => (
                    <option key={k.id} value={k.id}>
                      {k.name}{eingeteilt.has(k.id) ? ' (schon eingeteilt)' : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="rsm-field">
                <label className="rsm-label">Kampffläche *</label>
                <select className="rsm-input" required value={form.kampfflaeche_id}
                        onChange={e => setForm({ ...form, kampfflaeche_id: e.target.value })}>
                  <option value="">auswählen …</option>
                  {einteilung.flaechen.map(f => (
                    <option key={f.kampfflaeche_id} value={f.kampfflaeche_id}>{f.name}</option>
                  ))}
                </select>
              </div>
              <div className="rsm-field">
                <label className="rsm-label">Rolle</label>
                <select className="rsm-input" value={form.role}
                        onChange={e => setForm({ ...form, role: e.target.value })}>
                  {ROLLEN.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
            </div>

            <label className="rm-zeit-schalter">
              <input type="checkbox" checked={mitZeit} onChange={e => setMitZeit(e.target.checked)} />
              Nur für einen Zeitraum (sonst den ganzen Tag)
            </label>

            {mitZeit && (
              <div className="rsm-form-row">
                <div className="rsm-field">
                  <label className="rsm-label">Beginn</label>
                  <input className="rsm-input" type="datetime-local" required value={form.assignment_start}
                         onChange={e => setForm({ ...form, assignment_start: e.target.value })} />
                </div>
                <div className="rsm-field">
                  <label className="rsm-label">Ende</label>
                  <input className="rsm-input" type="datetime-local" required value={form.assignment_end}
                         onChange={e => setForm({ ...form, assignment_end: e.target.value })} />
                </div>
              </div>
            )}

            <div className="rsm-form-actions">
              <button type="submit" className="rsm-btn rsm-btn-primary" disabled={speichert}>
                {speichert ? 'Speichert …' : 'Zuteilen'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="rm-flaechen">
        {jeFlaeche.map(g => (
          <section key={g.schluessel} className="rm-flaeche" style={{ '--rm-farbe': g.farbe || '#8B0000' }}>
            <header className="rm-flaeche-kopf">
              <span className="rm-flaeche-punkt" />
              <span className="rm-flaeche-name">{g.titel}</span>
              <span className="rm-flaeche-zahl">{g.liste.length}</span>
            </header>
            {g.liste.length === 0 ? (
              <div className="rm-leer">niemand eingeteilt</div>
            ) : (
              <ul className="rm-liste">
                {g.liste.map(z => (
                  <li key={z.assignment_id} className="rm-eintrag">
                    <div className="rm-eintrag-text">
                      <span className="rm-name">{z.referee_name || 'unbekannt'}</span>
                      <span className="rm-rolle">
                        {rollenName(z.role)}
                        {z.assignment_start && ` · ${uhrzeit(z.assignment_start)}–${uhrzeit(z.assignment_end)} Uhr`}
                      </span>
                    </div>
                    <button className="rsm-card-del" onClick={() => entfernen(z)} title="Zuteilung entfernen">✕</button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        ))}
      </div>

      {ohneFlaeche.length > 0 && einteilung.flaechen.length > 0 && (
        <div className="rm-offen">
          <span className="rm-offen-titel">Noch nicht eingeteilt ({ohneFlaeche.length}):</span>{' '}
          {ohneFlaeche.map(k => k.name).join(', ')}
        </div>
      )}
    </div>
  );
};

export default RefereeManager;
